'use client';

import classnames from 'classnames';
import { useTranslations } from 'next-intl';
import React, { useCallback, useMemo } from 'react';

import { IconBell24 } from '@cineamo/ui/src/icons/ic-bell';
import { IconLoudBell24 } from '@cineamo/ui/src/icons/ic-loudBell.tsx';

import { useDomainConfiguration } from '@/providers/DomainConfigurationProvider';
import { NewsletterTopicPreselection, useNewsletterModal } from '@/providers/NewsletterModalProvider';

import { useNewsletterSubscription } from './useNewsletterSubscription';
import { useNewsletterTopics } from './useNewsletterTopics';

type BellSubscribeButtonProps = {
    topic?: NewsletterTopicPreselection;
    className?: string;
};

export default function BellSubscribeButton({ topic, className }: BellSubscribeButtonProps) {
    const { newsletterEnabled, subscriberStatus, openModal } = useNewsletterModal();
    const domainConfiguration = useDomainConfiguration();
    const cinema = domainConfiguration.cinema;
    const { addTopic, removeTopic } = useNewsletterSubscription(cinema?.id);
    const { allTopics } = useNewsletterTopics();
    const t = useTranslations('newsletter-bell');

    // A topic only exists while the operator keeps the matching newsletter section (see
    // SECTION_TYPE_BY_TOPIC in useNewsletterTopics). Without this the bell would offer a
    // subscription the modal cannot show, because the toggle is filtered out of the form.
    // While sections are still loading allTopics returns everything, so the bell does not flicker.
    const isTopicAvailable = useMemo(() => {
        if (!topic) return true;
        return allTopics.some(
            (available) => available.type === topic.type && (available.referenceId ?? '') === (topic.referenceId ?? '')
        );
    }, [allTopics, topic]);

    const isTopicSubscribed = useMemo(() => {
        if (!topic || !subscriberStatus?.subscribed || !subscriberStatus.topics) return false;
        return subscriberStatus.topics.some(
            (sub) => sub.type === topic.type && (sub.referenceId ?? '') === (topic.referenceId ?? '')
        );
    }, [topic, subscriberStatus]);

    const handleClick = useCallback(
        async (e: React.MouseEvent) => {
            e.stopPropagation();
            e.preventDefault();

            // Not yet a confirmed subscriber → open the signup modal, pre-selecting only the topic
            // this bell belongs to (CIN-13995). Sending them to /newsletter instead pre-selected
            // every topic, which subscribed them to far more than they asked for.
            if (!topic || !subscriberStatus?.subscribed) {
                openModal(topic ? [topic] : []);
                return;
            }

            // Already subscribed → toggle the topic in place via the existing
            // public subscription endpoint.
            if (isTopicSubscribed) {
                removeTopic(topic.type, topic.referenceId);
            } else {
                addTopic(topic);
            }
        },
        [topic, subscriberStatus, isTopicSubscribed, openModal, addTopic, removeTopic]
    );

    if (!newsletterEnabled || !isTopicAvailable) {
        return null;
    }

    return (
        <div
            className={classnames('size-36 shrink-0', className)}
            style={{ zIndex: 30 }}>
            <button
                type="button"
                onClickCapture={handleClick}
                className={classnames(
                    'size-36',
                    'inline-flex items-center justify-center rounded-4',
                    !isTopicSubscribed && 'bg-white/50',
                    'backdrop-blur-[8px]',
                    'pointer-events-auto cursor-pointer',
                    'transition-transform duration-200 active:scale-95',
                    'group/bell'
                )}
                style={isTopicSubscribed ? { backgroundColor: domainConfiguration.style.primaryColor } : undefined}
                aria-label={t('aria-label')}
                title={t('aria-label')}>
                {isTopicSubscribed ? (
                    <IconLoudBell24 className="size-24 transition-transform duration-300 ease-in-out scale-110" />
                ) : (
                    <IconBell24
                        className="size-24 transition-transform duration-300 ease-in-out group-hover/bell:animate-[bell-ring_0.6s_ease-in-out]"
                        fill="#000"
                    />
                )}
            </button>
        </div>
    );
}
